import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { MapPin } from "lucide-react";

interface GPSLocationProps {
    onLocation: (
        lat: number,
        lng: number,
        accuracy?: number,
        source?: "gps-watch" | "gps-current" | "network" | "ip"
    ) => void;
    lat: number | null;
    lng: number | null;
    autoRequest?: boolean;
}

export interface GPSLocationRef {
    triggerRequest: () => void;
}

type StepLog = {
    step: string;
    status: "success" | "failed" | "info";
    message: string;
};

const TOTAL_TIMEOUT = 600000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getGeoErrorDetails = (err: any) => {
    if (!err) {
        return {
            code: "UNKNOWN",
            title: "خطای نامشخص",
            reason: "خطایی بدون جزئیات از مرورگر دریافت شد.",
            solution: "GPS گوشی، دسترسی مرورگر و HTTPS بودن سایت را بررسی کنید.",
        };
    }

    if (err.code === 1) {
        return {
            code: "PERMISSION_DENIED / code 1",
            title: "دسترسی موقعیت بسته است",
            reason:
                "کاربر، مرورگر، یا تنظیمات گوشی اجازه دسترسی به Location را برای این سایت نداده است.",
            solution:
                "در تنظیمات مرورگر و گوشی، Location را برای این سایت روی Allow بگذارید. اگر سایت در Blocked است، حذفش کنید.",
        };
    }

    if (err.code === 2) {
        return {
            code: "POSITION_UNAVAILABLE / code 2",
            title: "موقعیت در دسترس نیست",
            reason:
                "GPS روشن است اما مرورگر نتوانسته موقعیت را از GPS یا سرویس مکانی دستگاه بگیرد. در ایران ممکن است سرویس‌های کمکی Location بدون VPN درست کار نکنند.",
            solution:
                "GPS روشن باشد، کاربر نزدیک پنجره یا فضای باز باشد، اینترنت پایدار باشد و دوباره تلاش کند.",
        };
    }

    if (err.code === 3) {
        return {
            code: "TIMEOUT / code 3",
            title: "زمان دریافت موقعیت تمام شد",
            reason:
                "مرورگر در زمان تعیین‌شده نتوانست مختصات معتبر بگیرد.",
            solution:
                "دوباره تلاش کنید، کاربر در فضای باز قرار بگیرد، یا انتخاب دستی روی نقشه را فعال کنید.",
        };
    }

    return {
        code: err.code ?? "UNKNOWN",
        title: "خطای نامشخص",
        reason: err.message || "مرورگر خطای مشخصی برنگرداند.",
        solution: "تنظیمات Location، HTTPS و مرورگر بررسی شود.",
    };
};

const GPSLocation = forwardRef<GPSLocationRef, GPSLocationProps>(
    ({ onLocation, lat, lng, autoRequest = false }, ref) => {
        const [loading, setLoading] = useState(false);
        const [statusText, setStatusText] = useState("");
        const [error, setError] = useState<string | null>(null);

        const watchId = useRef<number | null>(null);
        const runningRef = useRef(false);
        const logsRef = useRef<StepLog[]>([]);
        const lastErrorRef = useRef<any>(null);
        const onLocationRef = useRef(onLocation);

        useEffect(() => {
            onLocationRef.current = onLocation;
        }, [onLocation]);

        const addLog = (step: string, status: StepLog["status"], message: string) => {
            logsRef.current.push({ step, status, message });
            console.log(`[${status}] ${step}: ${message}`);
        };

        const clearWatch = () => {
            if (watchId.current !== null) {
                navigator.geolocation.clearWatch(watchId.current);
                watchId.current = null;
            }
        };

        const success = (
            latitude: number,
            longitude: number,
            accuracy?: number,
            source: "gps-watch" | "gps-current" | "network" | "ip" = "gps-current"
        ) => {
            clearWatch();

            onLocationRef.current(latitude, longitude, accuracy, source);

            setLoading(false);
            setError(null);
            setStatusText(
                `✅ موقعیت دریافت شد - ${latitude.toFixed(6)}, ${longitude.toFixed(6)}${accuracy ? ` - دقت: ${Math.round(accuracy)} متر` : ""
                }`
            );

            toast.success("موقعیت مکانی دریافت شد");
        };

        const showFinalError = async () => {
            const last = getGeoErrorDetails(lastErrorRef.current);

            const logsHtml = logsRef.current
                .map((log, index) => {
                    const icon =
                        log.status === "success" ? "✅" : log.status === "failed" ? "❌" : "ℹ️";

                    return `
            <div style="text-align:right;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:6px">
              <b>${index + 1}. ${icon} ${log.step}</b><br/>
              <small>${log.message}</small>
            </div>
          `;
                })
                .join("");

            await Swal.fire({
                title: "موقعیت مکانی دریافت نشد",
                icon: "error",
                html: `
          <div dir="rtl" style="text-align:right">
            <p><b>کد خطا:</b> ${last.code}</p>
            <p><b>علت:</b> ${last.title}</p>
            <p>${last.reason}</p>
            <p><b>راه‌حل پیشنهادی:</b> ${last.solution}</p>

            <hr/>

            <p><b>گزارش مراحل تست:</b></p>
            ${logsHtml}

            <hr/>

            <p style="font-size:12px;color:#666">
              نکته: اگر خطا code 1 باشد، با کد قابل دور زدن نیست. باید Permission مرورگر یا سایت باز شود.
            </p>
          </div>
        `,
                confirmButtonText: "متوجه شدم",
            });
        };

        const getCurrentPosition = (
            options: PositionOptions
        ): Promise<GeolocationPosition> => {
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, options);
            });
        };

        const tryPermissionCheck = async () => {
            addLog("بررسی HTTPS", "info", `Secure Context: ${window.isSecureContext}`);

            if (!window.isSecureContext) {
                throw new Error("سایت باید روی HTTPS باشد.");
            }

            addLog(
                "بررسی پشتیبانی مرورگر",
                "info",
                `Geolocation: ${!!navigator.geolocation}`
            );

            if (!navigator.geolocation) {
                throw new Error("مرورگر از Geolocation پشتیبانی نمی‌کند.");
            }

            try {
                if (navigator.permissions) {
                    const permission = await navigator.permissions.query({
                        name: "geolocation" as PermissionName,
                    });

                    addLog("بررسی Permission", "info", `Permission state: ${permission.state}`);

                    if (permission.state === "denied") {
                        const err: any = new Error("Location permission denied");
                        err.code = 1;
                        throw err;
                    }
                } else {
                    addLog("بررسی Permission", "info", "Permissions API پشتیبانی نمی‌شود.");
                }
            } catch (err: any) {
                lastErrorRef.current = err;
                throw err;
            }
        };

        const tryWatchGPS = async (remainingMs: number) => {
            setStatusText("⏳ مرحله ۱: دریافت GPS دقیق با watchPosition...");

            return new Promise<GeolocationPosition>((resolve, reject) => {
                let bestPosition: GeolocationPosition | null = null;

                const timeout = window.setTimeout(() => {
                    clearWatch();

                    if (bestPosition) {
                        addLog(
                            "watchPosition",
                            "success",
                            `بهترین موقعیت دریافت شد. accuracy=${bestPosition.coords.accuracy}`
                        );
                        resolve(bestPosition);
                    } else {
                        const err: any = new Error("watchPosition timeout");
                        err.code = 3;
                        reject(err);
                    }
                }, Math.min(remainingMs, 35000));

                watchId.current = navigator.geolocation.watchPosition(
                    (pos) => {
                        bestPosition = pos;

                        addLog(
                            "watchPosition",
                            "info",
                            `lat=${pos.coords.latitude}, lng=${pos.coords.longitude}, accuracy=${pos.coords.accuracy}`
                        );

                        if (pos.coords.accuracy <= 100) {
                            clearTimeout(timeout);
                            clearWatch();
                            resolve(pos);
                        }
                    },
                    (err) => {
                        clearTimeout(timeout);
                        clearWatch();
                        reject(err);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: Math.min(remainingMs, 35000),
                        maximumAge: 0,
                    }
                );
            });
        };

        const tryCurrentGPS = async (remainingMs: number) => {
            const attempts: PositionOptions[] = [
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
                { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 },
                { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
            ];

            for (let i = 0; i < attempts.length; i++) {
                if (Date.now() - startTimeRef.current >= TOTAL_TIMEOUT) {
                    throw new Error("Total timeout reached");
                }

                const options = attempts[i];

                setStatusText(`⏳ مرحله ۲: تست getCurrentPosition - تلاش ${i + 1}`);

                try {
                    const pos = await getCurrentPosition({
                        ...options,
                        timeout: Math.min(options.timeout || 10000, remainingMs),
                    });

                    addLog(
                        `getCurrentPosition تلاش ${i + 1}`,
                        "success",
                        `accuracy=${pos.coords.accuracy}`
                    );

                    return pos;
                } catch (err: any) {
                    lastErrorRef.current = err;

                    addLog(
                        `getCurrentPosition تلاش ${i + 1}`,
                        "failed",
                        `${getGeoErrorDetails(err).code} - ${getGeoErrorDetails(err).title}`
                    );

                    await sleep(800);
                }
            }

            throw lastErrorRef.current || new Error("getCurrentPosition failed");
        };

        const tryIPFallback = async () => {
            setStatusText("⏳ مرحله ۳: تست موقعیت تقریبی با IP...");

            const services = [
                {
                    name: "ipapi.co",
                    url: "https://ipapi.co/json/",
                    parse: (data: any) => ({
                        lat: data.latitude,
                        lng: data.longitude,
                    }),
                },
                {
                    name: "ipwho.is",
                    url: "https://ipwho.is/",
                    parse: (data: any) => ({
                        lat: data.latitude,
                        lng: data.longitude,
                    }),
                },
                {
                    name: "ipinfo.io",
                    url: "https://ipinfo.io/json",
                    parse: (data: any) => {
                        const [lat, lng] = String(data.loc || "").split(",").map(Number);
                        return { lat, lng };
                    },
                },
            ];

            for (const service of services) {
                try {
                    const res = await axios.get(service.url, { timeout: 7000 });
                    const parsed = service.parse(res.data);

                    if (Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng)) {
                        addLog(
                            `IP fallback - ${service.name}`,
                            "success",
                            `lat=${parsed.lat}, lng=${parsed.lng}`
                        );

                        return {
                            latitude: parsed.lat,
                            longitude: parsed.lng,
                        };
                    }

                    addLog(`IP fallback - ${service.name}`, "failed", "مختصات معتبر نبود.");
                } catch (err: any) {
                    addLog(
                        `IP fallback - ${service.name}`,
                        "failed",
                        err?.message || "خطا در سرویس IP"
                    );
                }
            }

            return null;
        };

        const startTimeRef = useRef(0);

        const requestLocation = useCallback(async () => {
            if (runningRef.current) return;

            runningRef.current = true;
            startTimeRef.current = Date.now();
            logsRef.current = [];
            lastErrorRef.current = null;

            setLoading(true);
            setError(null);
            setStatusText("⏳ شروع تست مرحله‌به‌مرحله موقعیت...");

            try {
                await tryPermissionCheck();

                let remaining = TOTAL_TIMEOUT - (Date.now() - startTimeRef.current);

                try {
                    const pos = await tryWatchGPS(remaining);

                    success(
                        pos.coords.latitude,
                        pos.coords.longitude,
                        pos.coords.accuracy,
                        "gps-watch"
                    );

                    addLog("نتیجه نهایی", "success", "GPS با watchPosition دریافت شد.");
                    return;
                } catch (err: any) {
                    lastErrorRef.current = err;

                    addLog(
                        "watchPosition",
                        "failed",
                        `${getGeoErrorDetails(err).code} - ${getGeoErrorDetails(err).title}`
                    );
                }

                remaining = TOTAL_TIMEOUT - (Date.now() - startTimeRef.current);

                if (remaining > 5000) {
                    try {
                        const pos = await tryCurrentGPS(remaining);

                        success(
                            pos.coords.latitude,
                            pos.coords.longitude,
                            pos.coords.accuracy,
                            "gps-current"
                        );

                        addLog("نتیجه نهایی", "success", "GPS با getCurrentPosition دریافت شد.");
                        return;
                    } catch (err: any) {
                        lastErrorRef.current = err;
                    }
                }

                remaining = TOTAL_TIMEOUT - (Date.now() - startTimeRef.current);

                if (remaining > 3000) {
                    const ip = await tryIPFallback();

                    if (ip) {
                        success(ip.latitude, ip.longitude, undefined, "ip");
                        addLog("نتیجه نهایی", "success", "موقعیت تقریبی با IP دریافت شد.");
                        return;
                    }
                }

                setLoading(false);
                setStatusText("");
                setError("موقعیت مکانی دریافت نشد.");
                await showFinalError();
            } catch (err: any) {
                lastErrorRef.current = err;

                addLog(
                    "خطای اصلی",
                    "failed",
                    err?.message || getGeoErrorDetails(err).title
                );

                setLoading(false);
                setStatusText("");
                setError(err?.message || "موقعیت مکانی دریافت نشد.");

                await showFinalError();
            } finally {
                clearWatch();
                runningRef.current = false;
            }
        }, []);

        useImperativeHandle(ref, () => ({ triggerRequest: requestLocation }), [
            requestLocation,
        ]);

        useEffect(() => {
            if (autoRequest && lat == null && lng == null) {
                requestLocation();
            }

            return () => clearWatch();
        }, []);

        return (
            <div className="space-y-2">
                {/* <label className="text-sm font-medium flex items-center gap-1">
                    <MapPin size={16} />
                    موقعیت مکانی
                </label> */}

                {lat != null && lng != null ? (
                    <div className="text-green-600 text-sm font-mono">
                        ✅ دریافت شد: {lat.toFixed(6)}, {lng.toFixed(6)}
                    </div>
                ) : loading ? (
                    // <div className="text-blue-600 text-sm">{statusText}</div>
                    ""
                ) : (
                    ""
                    // <div className="text-gray-500 text-sm">⏳ موقعیت دریافت نشده</div>
                    // <div className="text-gray-500 text-sm">⏳ موقعیت دریافت نشده</div>
                )}

                {/* {error && !loading && (
                    <div className="text-red-500 text-xs bg-red-50 p-2 rounded border border-red-200">
                        {error}
                    </div>
                )} */}
{/* 
                <button
                    type="button"
                    onClick={requestLocation}
                    disabled={loading}
                    className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                >
                    {loading ? "در حال تست موقعیت..." : "📡 تست و دریافت موقعیت"}
                </button> */}

                {/* <p className="text-xs text-gray-400">
                    * سیستم تا ۶۰ ثانیه همه روش‌های مجاز مرورگر را تست می‌کند.
                </p> */}
            </div>
        );
    }
);

GPSLocation.displayName = "GPSLocation";

export default GPSLocation;